/*
 * Home showcase slider. Replaces the original slider with a simple, infinite,
 * one-item-per-click carousel driven by GSAP. Arrows advance a single card and
 * the track loops seamlessly (three copies of the items; the position is reset
 * without animation once it crosses a boundary).
 *
 * Hover mirrors the original a-62 / a-63 actions on .slide-item-link:
 *   .slide-image     -> scale 1.1 + rotate 0.5deg
 *   .slide-content-bg -> opacity 0 -> 1
 * (0.7s ease in, 0.5s ease out).
 */
import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Showcase } from '@/types'

import { useEffect, useRef } from 'react'
import Link from 'next/link'

import { gsap, EASE, registerGsap } from '@/animations/core'

interface ShowcaseSliderProps {
    items: Showcase[]
}

export const ShowcaseSlider: FC<ShowcaseSliderProps> = ({ items }): ReactNode => {
    const trackRef = useRef<HTMLDivElement>(null)
    const indexRef = useRef(items.length) // start in the middle copy
    const goRef = useRef<(dir: number) => void>(() => {})

    const onArrowKey = (dir: number) => (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        goRef.current(dir)
    }

    const count = items.length
    const loop = count > 0 ? [...items, ...items, ...items] : []

    useEffect(() => {
        registerGsap()
        const track = trackRef.current
        if (!track || count === 0) return

        const step = (): number => {
            const kids = track.children
            if (kids.length < 2) return (kids[0] as HTMLElement)?.offsetWidth ?? 0
            return (kids[1] as HTMLElement).offsetLeft - (kids[0] as HTMLElement).offsetLeft
        }

        const place = () => gsap.set(track, { x: -indexRef.current * step() })
        place()

        const onResize = () => place()
        window.addEventListener('resize', onResize)

        // (Slide hover a-62/a-63 is handled by the shared GSAP hover engine.)

        /*
         * Navigation. Clicks are never dropped: each one retargets the tween
         * from wherever the track currently sits, so hammering an arrow keeps
         * advancing instead of ignoring the extra presses. The loop boundary is
         * normalised up front (shifting both the index and the current x by a
         * whole copy, which is visually identical) rather than in onComplete.
         */
        const go = (dir: number) => {
            const s = step()
            let idx = indexRef.current + dir
            let shift = 0

            if (idx >= count * 2) {
                idx -= count
                shift = count * s
            } else if (idx < count) {
                idx += count
                shift = -count * s
            }

            gsap.killTweensOf(track)

            if (shift !== 0) {
                const current = Number(gsap.getProperty(track, 'x')) || 0
                gsap.set(track, { x: current + shift })
            }

            indexRef.current = idx
            gsap.to(track, {
                x: -idx * s,
                duration: 0.55,
                ease: EASE,
                overwrite: 'auto'
            })
        }
        goRef.current = go

        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [count])

    if (count === 0) return null

    return (
        <div className='showcase-slider'>
            <div className='showcase-slider-viewport'>
                <div className='showcase-slider-track' ref={trackRef}>
                    {loop.map((item, i) => (
                        <div className='showcase-slide' key={`${item.slug}-${i}`}>
                            <Link className='slide-item-link mdl-inline-block' href={`/showcases/${item.slug}`}>
                                <div className='slide-item-content'>
                                    <div className='slide-item-subtitle' data-mdlfy-cms={`showcases:${item.id}:category`}>{item.category}</div>
                                    <div className='slide-item-title' data-mdlfy-cms={`showcases:${item.id}:name`}>{item.name}</div>
                                    <div className='slide-content-bg' />
                                </div>
                                <div className='slide-image-holder'>
                                    <img className='slide-image' src={item.mainImage} loading='lazy' alt={item.mainImageAlt || item.name} />
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>

            <div className='showcase-slider-arrows'>
                <div
                    className='slider-arrow grey-color'
                    role='button'
                    aria-label='Previous'
                    tabIndex={0}
                    onClick={() => goRef.current(-1)}
                    onKeyDown={onArrowKey(-1)}
                >
                    <span className='slider-arrow-image reverse' aria-hidden='true' />
                </div>
                <div
                    className='slider-arrow right-slider-arrow grey-color'
                    role='button'
                    aria-label='Next'
                    tabIndex={0}
                    onClick={() => goRef.current(1)}
                    onKeyDown={onArrowKey(1)}
                >
                    <span className='slider-arrow-image' aria-hidden='true' />
                </div>
            </div>
        </div>
    )
}
